import { useCallback, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { useSetRecoilState } from "recoil";
import { useParams } from "react-router-dom";

import { analytics } from "MVVM/Analytics";
import { MOMENT_CREATE_CLIP } from "MVVM/GraphQL/mutations/momentRequestClip";
import { PulseKickAtom } from "MVVM/State/clipStatus";
import { addPendingClipCreation } from "MVVM/Utilities/ClipLimits/pendingClipCreations";

// The server is the authority on eligibility, so a rejection comes back as a
// field on the mutation rather than something we can know before firing.
const isEligibilityError = (field?: string | null, code?: string | null) =>
  field === "eligibility" ||
  code === "QUOTA_EXCEEDED" ||
  code === "NOT_AUTHENTICATED";

export const useRequestClip = ({
  onIneligible,
  surface = "overview",
  matchId: matchIdProp,
}: {
  onIneligible: () => void;
  surface?: string;
  matchId?: string;
}) => {
  const params = useParams<{ matchId?: string }>();
  const matchId = matchIdProp ?? params.matchId;
  const [requested, setRequested] = useState<string[]>([]);
  const [failed, setFailed] = useState<Record<string, string>>({});
  const kickPulse = useSetRecoilState(PulseKickAtom);
  const [momentCreateClip] = useMutation(MOMENT_CREATE_CLIP);
  // Only an unfinished request blocks another; a finished one can be retried
  // once its render fails.
  const inFlight = useRef(new Set<string>());

  const forget = (momentId: string) =>
    setRequested((ids) => ids.filter((id) => id !== momentId));

  const requestClip = useCallback(
    // A key with its own input, e.g. a replay sequence, instead of a moment id.
    async (momentId: string, input?: Record<string, unknown>) => {
      // Guard the request itself, not just the label: a second click used to
      // fire a second mutation and spend a second credit.
      if (inFlight.current.has(momentId)) return;
      inFlight.current.add(momentId);

      setRequested((ids) =>
        ids.includes(momentId) ? ids : [...ids, momentId],
      );
      setFailed((all) => {
        if (!all[momentId]) return all;
        const next = { ...all };
        delete next[momentId];
        return next;
      });

      try {
        // momentCreateClip, not the deprecated momentRequestClip: this is the
        // mutation the server enforces the limit on, so it alone decides, and a
        // claimed daily clip goes through without the app having to know.
        const result = await momentCreateClip({
          variables: {
            momentCreateClipInput: input
              ? { matchId, ...input }
              : { ids: [momentId], matchId },
          },
        });
        const payload = result?.data?.momentCreateClip;
        const error = (payload?.errors || [])[0];

        if (error || payload?.success === false) {
          forget(momentId);
          if (isEligibilityError(error?.field, error?.code)) {
            analytics.track("Match Page - Clip Blocked", {
              matchId,
              momentId,
              surface,
              reason: "quota",
            });
            onIneligible();
            return;
          }
          setFailed((all) => ({
            ...all,
            [momentId]: error?.message || "That didn't work. Try again.",
          }));
          return;
        }

        // Counts against the local quota straight away, so the remaining count
        // is honest before the server catches up.
        analytics.track("Match Page - Clip Requested", {
          matchId,
          momentId,
          surface,
        });
        addPendingClipCreation();
        // Starts the app-wide clip status poll the rest of the app listens to.
        kickPulse((tick) => tick + 1);
        return payload?.trackId as string | undefined;
      } catch (err) {
        forget(momentId);
        setFailed((all) => ({
          ...all,
          [momentId]:
            err instanceof Error ? err.message : "That didn't work. Try again.",
        }));
      } finally {
        inFlight.current.delete(momentId);
      }
    },
    [momentCreateClip, kickPulse, onIneligible, matchId, surface],
  );

  return { requested, failed, requestClip };
};
